import React, { useEffect, useRef, useState } from "react";
import Description from "./components/Description";
import useDragging from "./useDrag";
import classNames from "classnames";

const BASE_CLASS =
  "border border-dashed border-light-neutral-2 rounded-base p-10 flex flex-col items-center bg-light-neutral-4";

export type CaFileInputProps = {
  name?: string;
  hoverTitle?: string;
  types?: string[];
  className?: string;
  children?: JSX.Element;
  maxSize?: number;
  minSize?: number;
  fileOrFiles?: File[] | File | null;
  disabled?: boolean | false;
  label?: string;
  multiple?: boolean | false;
  required?: boolean | false;
  onSizeError?: (arg0: string) => void;
  onTypeError?: (arg0: string) => void;
  onDrop: (arg0: File | File[]) => void;
  onSelect?: (arg0: File | File[]) => void;
  handleChange?: (arg0: File | File[] | File) => void;
  onDraggingStateChange?: (dragging: boolean) => void;
  dropMessageStyle?: React.CSSProperties | undefined;
};

const CaFileInput: React.FC<CaFileInputProps> = ({
  name,
  hoverTitle,
  types,
  handleChange,
  className,
  children,
  maxSize,
  minSize,
  fileOrFiles,
  onSizeError,
  onTypeError,
  onSelect,
  onDrop,
  disabled,
  label,
  multiple,
  required,
  onDraggingStateChange,
  dropMessageStyle,
}) => {
  const labelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploaded, setUploaded] = useState(false);
  const [currFiles, setFile] = useState<Array<File> | File | null>(null);
  const [error, setError] = useState(false);

  const validateFile = (file: File) => {
    if (types && !checkType(file, types)) {
      // types included and type not in them
      setError(true);
      if (onTypeError) onTypeError("File type is not supported");
      return false;
    }
    if (maxSize && file.size / 1000 / 1000 > maxSize) {
      setError(true);
      if (onSizeError) onSizeError("File size is too big");
      return false;
    }
    if (minSize && file.size / 1000 / 1000 < minSize) {
      setError(true);
      if (onSizeError) onSizeError("File size is too small");
      return false;
    }
    return true;
  };

  const handleChanges = (files: File | File[]): boolean => {
    let checkError = false;

    if (files) {
      if (files instanceof File) {
        checkError = !validateFile(files);
      } else {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          checkError = !validateFile(file) || checkError;
        }
      }

      if (checkError) return false;
      if (handleChange) handleChange(files);

      setFile(files);
      setUploaded(true);
      setError(false);

      return true;
    }
    return false;
  };

  const blockEvent = (ev: any) => {
    ev.preventDefault();
    ev.stopPropagation();
  };

  const handleClick = (ev: any) => {
    ev.stopPropagation();
    // eslint-disable-next-line no-param-reassign
    if (inputRef && inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  const handleInputChange = (ev: any) => {
    const allFiles = ev.target.files;
    const files = multiple ? allFiles : allFiles[0];
    const success = handleChanges(files);

    if (onSelect && success) onSelect(files);
  };

  const dragging = useDragging({
    labelRef,
    inputRef,
    multiple,
    handleChanges,
    onDrop,
  });

  useEffect(() => {
    onDraggingStateChange?.(dragging);
  }, [dragging]);

  useEffect(() => {
    if (fileOrFiles) {
      setUploaded(true);
      setFile(fileOrFiles);
    } else {
      if (inputRef.current) inputRef.current.value = "";
      setUploaded(false);
      setFile(null);
    }
  }, [fileOrFiles]);

  return (
    <div
      className={classNames(
        BASE_CLASS,
        className,
        disabled ? "is-disabled cursor-not-allowed" : "cursor-pointer",
      )}
      style={{
        minHeight: 200,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      ref={labelRef}
      onClick={blockEvent}
    >
      <input
        hidden
        onClick={handleClick}
        onChange={handleInputChange}
        accept={types ? types.map((type) => `.${type.toLowerCase()}`).join(",") : ""}
        ref={inputRef}
        type='file'
        name={name}
        disabled={disabled}
        multiple={multiple}
        required={required}
      />
      {dragging && (
        <div style={dropMessageStyle}>
          <span>{hoverTitle || "Drop Here"}</span>
        </div>
      )}
      {!children && (
        <>
          {/* <div error={error}> */}
          {error}
          <Description
            currFile={currFiles}
            uploaded={uploaded}
            typeError={error}
            disabled={disabled}
            label={label}
            maxSize={maxSize}
            types={types}
          />
          {/* <DrawTypes types={types} minSize={minSize} maxSize={maxSize} /> */}
        </>
      )}
      {children}
    </div>
  );
};

const checkType = (file: File, types: Array<string>): boolean => {
  const extension: string = file.name.split(".").pop() as string;
  const loweredTypes = types.map((type) => type.toLowerCase());
  return loweredTypes.includes(extension.toLowerCase());
};

export default CaFileInput;
